import { useState, useMemo } from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ChevronDown, Plus, X } from 'lucide-react';

export const FilterResponsesModal = ({
    questions = [],
    responses = [],
    onApply,
    onCancel,
    activeFilters = []
}) => {
    const [selectedFilters, setSelectedFilters] = useState(() => 
        activeFilters.map(filter => ({
            questionId: filter.questionId || '',
            answers: filter.answers || [],
            operator: 'includes'
        }))
    );

    // Memoize the unique answers map for each question type
    const uniqueAnswersMap = useMemo(() => {
        const answerMap = {};
        
        if (!questions || !responses) return answerMap;

        questions.forEach(question => {
            if (!question) return;

            if (question.type === 'multiple choice' || question.type === 'dropdown' || question.type === 'picture choice' || question.type === 'yes no') {
                answerMap[question.id] = question.options || [];
            }
            else if (question.type === 'checkbox list') {
                answerMap[question.id] = question.options || [];
            }
            else if (question.type === 'star rating') {
                answerMap[question.id] = [1, 2, 3, 4, 5];
            }
            else if (question.type === 'opinion scale' || question.type === 'nps') {
                const max = question.scaleMax || 10;
                answerMap[question.id] = Array.from({ length: max + 1 }, (_, i) => i);
            }
            else if (question.type === 'ranking') {
                answerMap[question.id] = question.options || [];
            }
            else if (question.type === 'long text' || question.type === 'short text' || question.type === 'email' || question.type === 'phone number' || question.type === 'url') {
                const uniqueAnswers = new Set();
                responses.forEach(response => {
                    if (!response?.answers) return;
                    const answer = response.answers[question.id];
                    if (answer && typeof answer === 'string' && answer.trim()) {
                        uniqueAnswers.add(answer);
                    }
                });
                answerMap[question.id] = Array.from(uniqueAnswers).sort();
            }
        });
        
        return answerMap;
    }, [questions, responses]);

    const addNewFilter = () => {
        setSelectedFilters(prev => [...prev, {
            questionId: '',
            answers: [],
            operator: 'includes'
        }]);
    };

    const removeFilter = (index) => {
        setSelectedFilters(prev => prev.filter((_, i) => i !== index));
    };

    const updateFilter = (index, field, value) => {
        setSelectedFilters(prev => prev.map((filter, i) => {
            if (i === index) {
                if (field === 'questionId') {
                    return {
                        ...filter,
                        questionId: value,
                        answers: [] // Reset answers when question changes
                    };
                }
                return { ...filter, [field]: value };
            }
            return filter;
        }));
    };

    const getAvailableAnswers = (questionId) => {
        return uniqueAnswersMap[questionId] || [];
    };

    // Get already selected questions
    const selectedQuestionIds = selectedFilters.map(f => f.questionId).filter(Boolean);

    // Filter questions to show only filterable types
    const filterableQuestions = questions.filter(q => 
        ['multiple choice', 'star rating', 'long text', 'short text', 'checkbox list', 'opinion scale', 'dropdown', 'picture choice', 'ranking', 'email', 'phone number', 'yes no', 'nps', 'url', 'slider'].includes(q.type)
    );

    return (
        <div className="space-y-6 pr-2">
            <div className="min-h-[50vh] max-h-[80vh] overflow-y-auto">
                <div className="text-sm text-gray-600">
                    Show me all responses where...
                </div>

                {/* Filters */}
                <div className="space-y-4 pl-1">
                    {selectedFilters.map((filter, index) => (
                        <div key={index} className="relative">
                            <div className="space-y-2">
                                <div className="flex items-start justify-between">
                                    {/* Question Select */}
                                    <div className="flex-1">
                                        <Listbox
                                            value={filter.questionId}
                                            onChange={(value) => updateFilter(index, 'questionId', value)}
                                        >
                                            <div className="relative">
                                                <ListboxButton className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left border rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
                                                    <span className={`block truncate max-w-80 ${!filter.questionId && 'text-gray-400'}`}>
                                                        {filter.questionId
                                                            ? questions.find(q => q.id === filter.questionId)?.text
                                                            : 'Select question or data...'}
                                                    </span>
                                                    <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                                </ListboxButton>
                                                <ListboxOptions anchor="bottom" className="z-[999] w-[var(--button-width)] mt-1 bg-white rounded-md shadow-lg !max-h-60 overflow-auto border focus:outline-none py-1">
                                                    {filterableQuestions.map((question) => (
                                                        <ListboxOption
                                                            key={question.id}
                                                            value={question.id}
                                                            disabled={selectedQuestionIds.includes(question.id) && question.id !== filter.questionId}
                                                            className={({ active, selected, disabled }) => `
                                                                    cursor-pointer select-none relative py-2 px-3 text-sm
                                                                    ${active && !disabled ? 'bg-cyan-50 text-cyan-900' : 'text-gray-900'}
                                                                    ${selected ? 'bg-cyan-50' : ''}
                                                                    ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}
                                                                    truncate
                                                                `}
                                                        >
                                                            {question.text}
                                                        </ListboxOption>
                                                    ))}
                                                </ListboxOptions>
                                            </div>
                                        </Listbox>
                                    </div>

                                    {selectedFilters.length > 1 && (
                                        <button
                                            onClick={() => removeFilter(index)}
                                            className="p-2 text-gray-400 hover:text-gray-600 focus:outline-none ml-2 flex-shrink-0"
                                        >
                                            <X className="h-5 w-5" />
                                        </button>
                                    )}
                                </div>

                                {/* Answer Select with Includes Badge */}
                                {filter.questionId && (
                                    <div className="flex items-center gap-2 mr-11">
                                        <div className="bg-gray-100 px-2 py-1 rounded text-sm text-gray-600">
                                            includes
                                        </div>
                                        <div className="flex-1">
                                            <Listbox
                                                value={filter.answers}
                                                onChange={(value) => updateFilter(index, 'answers', value)}
                                                multiple
                                            >
                                                <div className="relative">
                                                    <ListboxButton className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left border rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
                                                        <div className="flex flex-wrap gap-1">
                                                            {filter.answers.length > 0 ? (
                                                                filter.answers.map((answer, i) => (
                                                                    <span key={i} className="bg-gray-100 px-2 py-0.5 rounded text-sm flex items-center gap-1">
                                                                        {answer}
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                updateFilter(index, 'answers', filter.answers.filter(a => a !== answer));
                                                                            }}
                                                                            className="hover:text-gray-700"
                                                                        >
                                                                            <X className="h-3 w-3" />
                                                                        </button>
                                                                    </span>
                                                                ))
                                                            ) : (
                                                                <span className="text-gray-400">Select answers...</span>
                                                            )}
                                                        </div>
                                                        <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                                    </ListboxButton>
                                                        <ListboxOptions 
                                                            anchor="bottom" 
                                                            className="absolute z-[999] w-[var(--button-width)] mt-1 bg-white rounded-md shadow-lg !max-h-52 overflow-auto border focus:outline-none py-1"
                                                        >
                                                            {getAvailableAnswers(filter.questionId).map((answer) => (
                                                                <ListboxOption
                                                                    key={answer}
                                                                    value={answer}
                                                                    className={({ active, selected }) => `
                                                                        cursor-pointer select-none relative py-2 px-3 text-sm text-gray-900
                                                                        ${active ? 'bg-cyan-50 text-cyan-900' : ''}
                                                                        ${selected ? 'bg-cyan-50' : ''}
                                                                        my-1
                                                                    `}
                                                                >
                                                                    {answer}
                                                                </ListboxOption>
                                                            ))}
                                                        </ListboxOptions>
                                                </div>
                                            </Listbox>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {index < selectedFilters.length - 1 && (
                                <div className="my-4 text-sm text-gray-500">
                                    and
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Add Filter Button */}
                <button
                    onClick={addNewFilter}
                    className="mt-2 flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700 focus:outline-none"
                >
                    <Plus className="h-4 w-4" />
                    Add another filter
                </button>
            </div>
            {/* Footer */}
            <div className="flex items-center justify-end pt-4 border-t mt-4">
                <div className="flex gap-3">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onApply(selectedFilters)}
                        disabled={!selectedFilters.some(f => f.questionId && f.answers.length)}
                        className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Apply
                    </button>
                </div>
            </div>
        </div>
    );
} 